import { Controller } from '@hotwired/stimulus'

// The on-the-floor PRP checklist runner (CMP-201b). A slimmer sibling of the
// audit runner: tap pass/fail/na per item, answers sync in the background with
// a localStorage offline buffer that replays on reconnect, one section at a
// time. No findings sheet, voice, or live score — a checklist is a categorical
// pass/fail, and its outcome is computed server-side on finalize.
export default class extends Controller {
  static targets = ['progress', 'connection', 'section', 'sectionLabel']

  static values = {
    answerUrl: String,
    editable: Boolean
  }

  connect () {
    this.answers = this.readAnswersFromDom()
    this.mergeBufferedAnswers()
    this.currentSection = 0
    this.showSection(0)
    this.updateProgress()
    this.boundFlush = () => this.flushAnswers()
    window.addEventListener('online', this.boundFlush)
    this.updateConnection(navigator.onLine)
  }

  disconnect () {
    window.removeEventListener('online', this.boundFlush)
  }

  // ---- answering -------------------------------------------------

  answer (event) {
    const { itemId, answer } = event.currentTarget.dataset
    this.answers[itemId] = answer
    this.updateProgress()
    this.scheduleSave()
  }

  updateProgress () {
    if (this.hasProgressTarget) this.progressTarget.textContent = Object.keys(this.answers).length
  }

  // ---- section navigation ---------------------------------------

  previousSection () { this.showSection(this.currentSection - 1) }
  nextSection () { this.showSection(this.currentSection + 1) }

  showSection (index) {
    const sections = this.sectionTargets
    if (sections.length === 0) return
    this.currentSection = Math.max(0, Math.min(index, sections.length - 1))
    sections.forEach((section, i) => section.classList.toggle('hidden', i !== this.currentSection))
    if (this.hasSectionLabelTarget) {
      this.sectionLabelTarget.textContent = `${this.currentSection + 1} / ${sections.length}`
    }
  }

  // ---- finalize -------------------------------------------------

  // Flush any pending answers before the finalize form submits, so the locked
  // outcome is computed over the complete answer set.
  async finalize (event) {
    event.preventDefault()
    await this.flushAnswers()
    // requestSubmit (not submit) fires a submit event Turbo intercepts, so the
    // finalize goes through Turbo's navigation rather than a raw form post.
    event.target.requestSubmit()
  }

  // ---- persistence + offline buffer -----------------------------

  scheduleSave () {
    this.persistLocally()
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this.flushAnswers(), 800)
  }

  async flushAnswers () {
    if (!this.editableValue) return
    try {
      const response = await fetch(this.answerUrlValue, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ answers: this.answers })
      })
      if (!response.ok) throw new Error('answer sync failed')
      this.updateConnection(true)
    } catch (error) {
      this.updateConnection(false)
    }
  }

  persistLocally () {
    try { window.localStorage.setItem(this.bufferKey, JSON.stringify(this.answers)) } catch (e) {}
  }

  mergeBufferedAnswers () {
    try {
      const buffered = JSON.parse(window.localStorage.getItem(this.bufferKey) || '{}')
      let changed = false
      for (const [itemId, ans] of Object.entries(buffered)) {
        if (this.answers[itemId] !== ans) { this.answers[itemId] = ans; changed = true }
      }
      if (changed) this.flushAnswers()
    } catch (e) {}
  }

  updateConnection (online) {
    if (!this.hasConnectionTarget) return
    this.connectionTarget.textContent = online
      ? this.connectionTarget.dataset.online || ''
      : this.connectionTarget.dataset.offline || '⚠'
  }

  // ---- helpers ---------------------------------------------------

  readAnswersFromDom () {
    const answers = {}
    this.element.querySelectorAll('input[type=radio][data-item-id]:checked').forEach(input => {
      answers[input.dataset.itemId] = input.dataset.answer
    })
    return answers
  }

  get bufferKey () {
    return `compliance:program-runner:${this.answerUrlValue}`
  }

  get csrfToken () {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
